import React from 'react';
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    PDFDownloadLink,
    Font,
    Image,
} from '@react-pdf/renderer';
import { useLocation, useNavigate } from 'react-router-dom';
import logo from '../logo.png';
import header from '../header.png';
import watermark from '../Watermark.png';
import contact from '../contact.png';
import '../css/TDSReportPdf.css';

// Register the Roboto font globally
Font.register({
    family: 'Roboto',
    src: 'https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf',
});

// Define styles for the PDF document
const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: 'Roboto',
        fontSize: 12,
        lineHeight: 1.5,
        position: 'relative', // Ensure relative positioning for overlay elements
    },
    header: {
        textAlign: 'center',
        marginBottom: 140,
        position: 'relative',
        paddingBottom: 15,
    },
    logo: {
        position: 'absolute',
        top: 10,
        left: 10,
        width: 70,
        height: 70,
    },
    head: {
        position: 'absolute',
        left: '25%',
        width: '60%',
        height: 'auto',
    },

    date: {
        textAlign: 'right',
        marginTop: 12,
    },

    title: {
        fontSize: 12,
        fontWeight: 'bold',
    },
    section: {
        marginTop: 50,
        marginVertical: 10,
        paddingHorizontal: 8,
    },
    summary: {
        fontSize: 12,
        marginBottom: 10,
        textAlign: 'center',
        textDecoration: 'underline',
        fontWeight: 'bold',
    },
    tableRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
    },
    tableTextLabel: {
        fontWeight: 'bold',
    },
    tableTextValue: {
        textAlign: 'right',
    },

    watermark: {
        position: 'absolute',
        top: '25%',
        width: '90%',
        opacity: 0.8, // Reduce opacity for the watermark
    },

    foot: {
        textAlign: 'center',
        marginTop: 26,
        fontSize: 10,
        paddingTop: 10,
    },

    note: {
        marginTop: 55,
        textAlign: 'left',
        fontSize: 7,
    },

    footer: {
        textAlign: 'center',
        marginTop: 65,
        bottomTop: 25,
    },

    contact: {
        position: 'absolute',
        bottomTop: 25,
        width: '100%',
    }
});

// PDF Document Component
const TDSReportPDF = ({ agentName, taxableAmount, tdsAmount, fiscalYear, panNo }) => {
    const today = new Date().toLocaleDateString('ne-NP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    return (
        <Document>
            <Page style={styles.page}>
                {/* Watermark */}
                <Image src={watermark} style={styles.watermark} />
                {/* Header Section */}
                <View style={styles.header}>
                    <View style={styles.logoContainer}>
                        <Image src={logo} style={styles.logo} />
                        <View>
                            <Image src={header} style={styles.head} />
                        </View>
                    </View>
                </View>

                {/* Subject Section */}
                <View style={styles.section}>
                    <Text style={styles.date}>Date: {today}</Text>
                    <Text style={styles.title}>City Wallet Pvt. Ltd.</Text>
                    <Text>Kamaladi, Kathmandu, Nepal</Text>
                    <Text>PAN No: 619727437</Text>

                    <Text style={styles.summary}>Subject: TDS Certificate</Text>
                    <Text>Dear Sir/Madam,</Text>
                    <Text>
                        With reference to the above-mentioned subject, we hereby certify that the details of Tax Deducted at Source (TDS) applicable to your esteemed company for the fiscal year ending {fiscalYear} are outlined below. This document is provided to assist you with your tax compliance and record-keeping requirements. Please review the enclosed details carefully and contact us should you have any inquiries.
                    </Text>
                </View>

                {/* Summary of Tax Deduction */}
                <View style={styles.section}>
                    <Text style={styles.summary}>Summary of Tax Deduction:</Text>
                    <View style={styles.tableRow}>
                        <Text style={styles.tableTextLabel}>Financial Year:</Text>
                        <Text style={styles.tableTextValue}>{fiscalYear}</Text>
                    </View>
                    <View style={styles.tableRow}>
                        <Text style={styles.tableTextLabel}>Agent's Name:</Text>
                        <Text style={styles.tableTextValue}>{agentName}</Text>
                    </View>
                    <View style={styles.tableRow}>
                        <Text style={styles.tableTextLabel}>PAN No:</Text>
                        <Text style={styles.tableTextValue}>{panNo}</Text>
                    </View>
                    <View style={styles.tableRow}>
                        <Text style={styles.tableTextLabel}>
                            Taxable Amount:
                        </Text>
                        <Text style={styles.tableTextValue}>{taxableAmount}</Text>
                    </View>
                    <View style={styles.tableRow}>
                        <Text style={styles.tableTextLabel}>TDS Amount:</Text>
                        <Text style={styles.tableTextValue}>{tdsAmount}</Text>
                    </View>
                    <View style={styles.foot}>
                        <Text>Thank you for your kind cooperation.</Text>
                        <Text>Greetings from, City Wallet Pvt. Ltd.</Text>
                        <Text style={styles.note}>Note: This is a system generated report, please contact our head-office for further info.</Text>
                    </View>

                </View>

                {/* Footer */}

                <View style={styles.footer}>
                    <Image src={contact} style={styles.contact} />
                </View>

            </Page>
        </Document>
    );
};

// Wrapper Component for Download Link
const TDSReportPDFWrapper = ({ updatedTdsData }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { updatedTdsData: locationTdsData, isVerified } = location.state || {};

    // Consolidate data from props or location state
    const data = updatedTdsData || locationTdsData || {};
    const isReady = Boolean(isVerified || data.isVerified);

    // Validation for necessary data and verification status
    if (!data || !isReady || !data.taxableAmount || !data.tdsAmount) {
        return (
            <div className="pdf-page">
                <div className="pdf-shell pdf-shell-single">
                    <section className="pdf-card pdf-empty-state">
                        <img src={logo} alt="City Wallet Logo" className="pdf-empty-logo" />
                        <span className="pdf-badge pdf-badge-warn">Action needed</span>
                        <h2>Your TDS certificate is not ready yet</h2>
                        <p>
                            We could not find a verified TDS report in this session. Please complete the form and OTP verification again to continue.
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="pdf-primary-button"
                        >
                            Back to TDS Form
                        </button>
                    </section>
                </div>
            </div>
        );
    }

    const summaryItems = [
        { label: 'Agent Name', value: data.agentName || 'N/A' },
        { label: 'PAN Number', value: data.panNo || 'N/A' },
        { label: 'Fiscal Year', value: data.fiscalYear || 'N/A' },
        { label: 'Taxable Amount', value: data.taxableAmount || 'N/A' },
        { label: 'TDS Amount', value: data.tdsAmount || 'N/A' },
    ];

    return (
        <div className="pdf-page">
            <div className="pdf-shell">
                <section className="pdf-intro">
                    <span className="pdf-badge">Certificate Ready</span>
                    <h1>Your TDS certificate is ready to download</h1>
                    <p className="pdf-copy">
                        Review the summary below and download the official certificate for your records. The PDF includes your verified PAN and fiscal year details.
                    </p>

                    <div className="pdf-highlight-grid">
                        <div className="pdf-highlight-card">
                            <span className="pdf-highlight-label">Verified PAN</span>
                            <strong>{data.panNo || 'N/A'}</strong>
                        </div>
                        <div className="pdf-highlight-card">
                            <span className="pdf-highlight-label">Fiscal Year</span>
                            <strong>{data.fiscalYear || 'N/A'}</strong>
                        </div>
                        <div className="pdf-highlight-card">
                            <span className="pdf-highlight-label">TDS Amount</span>
                            <strong>{data.tdsAmount || 'N/A'}</strong>
                        </div>
                    </div>
                </section>

                <section className="pdf-card">
                    <div className="pdf-card-header">
                        <div className="pdf-brand">
                            <img src={logo} alt="City Wallet Logo" className="pdf-logo" />
                            <div>
                                <h2>TDS Certificate</h2>
                                <p>Generated after successful PAN, mobile, and OTP verification.</p>
                            </div>
                        </div>
                        <span className="pdf-badge pdf-badge-soft">Download Center</span>
                    </div>

                    <div className="pdf-summary">
                        {summaryItems.map((item) => (
                            <div key={item.label} className="pdf-summary-row">
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                    </div>

                    <div className="pdf-note">
                        <p>
                            Please review the certificate details before downloading. If anything looks incorrect, return to the form and verify the information again.
                        </p>
                    </div>

                    <div className="pdf-actions">
                        <PDFDownloadLink
                            document={
                                <TDSReportPDF
                                    agentName={data.agentName || 'N/A'}
                                    taxableAmount={data.taxableAmount || 'N/A'}
                                    tdsAmount={data.tdsAmount || 'N/A'}
                                    fiscalYear={data.fiscalYear || 'N/A'}
                                    panNo={data.panNo || 'N/A'}
                                />
                            }
                            fileName={`TDS_Report_${data.agentName || 'N/A'}_${data.fiscalYear || 'N/A'}.pdf`}
                            className="pdf-download-link"
                        >
                            {({ loading, error }) => {
                                if (error) {
                                    return (
                                        <span className="pdf-inline-error">
                                            We could not generate the PDF right now. Please try again.
                                        </span>
                                    );
                                }

                                return (
                                    <button className="pdf-primary-button">
                                        {loading ? 'Generating TDS Certificate...' : 'Download TDS Certificate'}
                                    </button>
                                );
                            }}
                        </PDFDownloadLink>

                        <button
                            type="button"
                            className="pdf-secondary-button"
                            onClick={() => navigate('/')}
                        >
                            Back to Form
                        </button>
                    </div>

                    <div className="pdf-footer-note">
                        <p>
                            System-generated certificate. Keep this file for tax records and compliance reference.
                        </p>
                    </div>
                </section>
            </div>
        </div>
        );
};

export default TDSReportPDFWrapper;
